<script setup lang="ts">
import { computed, ref, useTemplateRef, type HTMLAttributes } from 'vue'
import { useDropZone } from '@vueuse/core'
import { CloudUpload, FileCheck, X } from '@lucide/vue'
import { cn } from '@ds/lib/utils'
// Reached directly rather than through `@ds`: the barrel re-exports this file,
// and importing it back would close a cycle through a Rolldown chunk boundary.
import { Button, buttonVariants } from '../../primitives/button'

/*
 * A pattern, not a primitive: hand-written and composed from Button rather than
 * vendored from the registry, so `shadcn-vue diff` has nothing to say about it
 * and it must not sit in primitives/ pretending otherwise.
 *
 * Hand-written because there is nothing upstream to take: shadcn-vue ships
 * `InputFile` only as a `registry:example` — a demo composition, not an
 * installable component — and Reka UI has no file-upload family. Re-check both
 * before extending this.
 */
const props = defineProps<{
  /** Comma-separated extension list, e.g. '.qfx,.ofx'. */
  accept: string
  modelValue?: File
  /** Headline inside the zone. Defaults to a generic prompt. */
  label?: string
  /** Sub-line. Defaults to the accepted extensions. */
  hint?: string
  /** Message from the caller — server-side rejection, say. Shown as-is. */
  error?: string
  disabled?: boolean
  class?: HTMLAttributes['class']
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: File | undefined): void
}>()

// Attributes land on the <input>, not the wrapper — that is where `name`,
// `required` and test hooks such as data-refid belong.
defineOptions({ inheritAttrs: false })

const dropZoneRef = useTemplateRef<HTMLElement>('dropZoneRef')

const rejection = ref<string>()

const acceptedExtensions = computed(() =>
  props.accept
    .split(',')
    .map(extension => extension.trim().toLowerCase())
    .filter(Boolean),
)

const acceptedLabel = computed(() => acceptedExtensions.value.join(' or '))

const message = computed(() => props.error || rejection.value)

const fileSize = computed(() => {
  if (!props.modelValue) return ''

  const kilobytes = props.modelValue.size / 1024
  return kilobytes < 1024
    ? `${Math.max(1, Math.round(kilobytes))} KB`
    : `${(kilobytes / 1024).toFixed(1)} MB`
})

const zoneClass = computed(() => {
  if (message.value) return 'border-destructive bg-card'
  if (isOverDropZone.value) return 'border-ring bg-accent'
  return 'border-input bg-card'
})

/*
 * A drop bypasses `accept`, which the browser only enforces in the picker, so
 * the extension is re-checked here. Without it a dropped .pdf uploads and comes
 * back as a far less useful server-side failure.
 */
const setFile = (candidate?: File) => {
  if (!candidate) return

  const extension = candidate.name
    .slice(candidate.name.lastIndexOf('.'))
    .toLowerCase()

  if (!acceptedExtensions.value.includes(extension)) {
    rejection.value = `${candidate.name} is not ${acceptedLabel.value}.`
    return
  }

  rejection.value = ''
  emits('update:modelValue', candidate)
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop: files => {
    if (props.disabled) return
    setFile(files?.[0])
  },
})

const handleChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  setFile(input.files?.[0])
  // Let the same file be re-picked after a clear.
  input.value = ''
}

const handleClear = () => {
  rejection.value = ''
  emits('update:modelValue', undefined)
}
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5 font-ds', props.class)">
    <!--
      The drop zone is a mouse-only enhancement layered over a real file input:
      the input stays in the DOM, focusable and label-associated, so keyboard
      and screen-reader users get the ordinary picker. `useDropZone` owns the
      enter/leave bookkeeping — a hand-rolled dragleave fires on every child
      element and makes the highlight flicker.
    -->
    <div
      ref="dropZoneRef"
      :class="cn(
        'flex flex-col items-center justify-center gap-3 min-w-0 px-6 py-7',
        'border border-dashed rounded-ds-md text-center',
        'transition-[background-color,border-color] duration-ds-fast ease-ds-standard',
        zoneClass,
      )"
    >
      <template v-if="modelValue">
        <span class="inline-flex items-center justify-center size-10 bg-accent text-accent-foreground rounded-ds-sm">
          <FileCheck class="size-5" />
        </span>
        <!--
          `block w-full min-w-0`, not `max-w-full`: this is a span, so on an
          inline box max-width does nothing, and as a flex item under
          `items-center` it sizes to fit-content — a long filename then widens
          the panel and its dialog. A definite width is also what `truncate`
          needs to have anything to truncate against.
        -->
        <span class="block w-full min-w-0">
          <span
            class="block truncate text-sm font-bold text-foreground"
            :title="modelValue.name"
          >
            {{ modelValue.name }}
          </span>
          <span class="block text-xs font-medium text-muted-foreground">
            {{ fileSize }}
          </span>
        </span>
        <Button
          variant="secondary"
          size="sm"
          class="border-border font-medium"
          :disabled="disabled"
          @click="handleClear"
        >
          <X class="size-3.5" />
          Remove
        </Button>
      </template>

      <template v-else>
        <span class="inline-flex items-center justify-center size-10 bg-muted text-muted-foreground rounded-ds-sm">
          <CloudUpload class="size-5" />
        </span>
        <span class="block w-full min-w-0">
          <span class="block text-sm font-bold text-foreground">
            {{ label || 'Drag a file here' }}
          </span>
          <span class="block text-xs font-medium text-muted-foreground">
            {{ hint || `${acceptedLabel} files, up to one at a time` }}
          </span>
        </span>
        <!--
          A label rather than a button: it activates the input it wraps, so the
          native picker opens without scripting a click.
        -->
        <label
          :class="cn(
            buttonVariants({ variant: 'secondary', size: 'sm' }),
            'border-border font-medium focus-within:shadow-ds-focus',
            disabled && 'pointer-events-none opacity-50',
          )"
        >
          Choose a file
          <input
            v-bind="$attrs"
            type="file"
            class="sr-only"
            :accept="accept"
            :disabled="disabled"
            @change="handleChange"
          >
        </label>
      </template>
    </div>

    <p
      v-if="message"
      class="text-xs font-medium text-destructive-subtle-foreground"
      role="alert"
    >
      {{ message }}
    </p>
  </div>
</template>
